import {
  createEffect,
  createResource,
  createMemo,
  on,
  mergeProps,
  onCleanup,
  onMount,
} from "solid-js";
import {
  createContext,
  provide,
  consume,
  type ICustomElement,
} from "component-register";
import debounce from "lodash.debounce";
import {
  customShadowlessElement,
  correctElementType,
} from "@brytdesigns/web-component-core/utils";

interface SearchState {
  sectionId: string;
  terms?: string;
  delay: number;
  page: number;
  element: HTMLElement & ICustomElement;
}

interface SearchContextState {
  textResults: () => string | undefined;
  terms: () => string;
}

interface SearchContextActions {
  getResultsMarkup: () => string;
  createTermsUpdater: () => (terms: string) => void;
  updatePage: (page: number) => number | void;
}

type SearchContextValue = [SearchContextState, SearchContextActions];

interface SearchSectionProps {
  sectionId: string;
  terms?: string;
  delay: number;
  page: number;
}

interface SearchListenerProps {
  on: keyof HTMLElementEventMap;
  preventDefault?: boolean;
}

interface ComponentOptions {
  element: HTMLElement & ICustomElement;
}

function updateSearchParam(
  url: URL,
  { key, value }: { key: string; value: string | number | null | undefined },
): URL {
  const newUrl = new URL(url);

  if (!value) {
    newUrl.searchParams.delete(key);
  } else if (url.searchParams.has(key)) {
    newUrl.searchParams.set(key, String(value));
  } else {
    newUrl.searchParams.append(key, String(value));
  }

  return newUrl;
}

function updateSearchParams(
  url: URL,
  params: Record<string, string | number | null | undefined>,
): URL {
  for (const [key, value] of Object.entries(params)) {
    url = updateSearchParam(url, { key, value });
  }
  return url;
}

const SearchContext = createContext(
  (state: SearchState): SearchContextValue => {
    const url = createMemo<string>(() => {
      let url = new URL(window.location.href);
      url = updateSearchParams(url, {
        q: state.terms || "",
        section_id: state.sectionId,
        page: state.page > 1 ? state.page : 0,
      });
      return url.href;
    });

    function updateUrl(url: URL): URL {
      url = updateSearchParams(url, { section_id: "" });
      window.history.replaceState({}, "", url.toString());
      return url;
    }

    const [textResults] = createResource<string, string>(url, async (url) => {
      const urlObj = new URL(url);
      const res = await fetch(urlObj);
      const text = await res.text();

      updateUrl(urlObj);
      return text;
    });

    const getResultsMarkup = (): string => {
      const text = textResults();
      if (!text) return "";

      const doc = new DOMParser().parseFromString(text, "text/html");
      const section = doc.querySelector(`#shopify-section-${state.sectionId}`);

      if (!section) return "";

      const clone = section.cloneNode(true) as HTMLElement;
      const results = clone.querySelector("search-results")?.innerHTML;

      return results || "";
    };

    const createTermsUpdater = () =>
      debounce((terms: string) => {
        if (state.terms === terms) return;
        state.element.setAttribute("terms", terms || "");
      }, state.delay);

    const updatePage = (page: number) => {
      if (page === state.page) return;
      state.element.setAttribute("page", String(page));
      return page;
    };

    return [
      {
        textResults,
        terms: () => state.terms || "",
      },
      {
        getResultsMarkup,
        createTermsUpdater,
        updatePage,
      },
    ];
  },
);

const useSearch = (
  element: HTMLElement & ICustomElement,
): SearchContextValue => {
  const context = consume(SearchContext, element);
  if (!context) throw new Error("search-section: context is required");
  return context;
};

function SearchSection(props: SearchSectionProps, options: ComponentOptions) {
  const { element } = options;

  if (!props.sectionId) {
    console.warn('search-section: "section-id" attribute is required');
    return;
  }

  provide(SearchContext, mergeProps(props, { element }), element);

  createEffect(
    on(
      () => props.terms,
      () => element.setAttribute("page", "1"),
    ),
  );
}

function SearchListener(props: SearchListenerProps, options: ComponentOptions) {
  const { element } = options;
  const [, { createTermsUpdater }] = useSearch(element);

  createEffect(
    on(
      () => ({
        on: props.on,
        preventDefault: props.preventDefault,
        updateTerms: createTermsUpdater(),
      }),
      ({ on, preventDefault, updateTerms }) => {
        if (!on) {
          console.warn('search-listener: "on" attribute is required');
          return;
        }

        const handleEvent = (event: Event) => {
          if (preventDefault) event.preventDefault();

          if (on === "submit") {
            const form = event.target as HTMLFormElement;
            const formData = new FormData(form);
            const q = formData.get("q");
            return updateTerms(String(q ?? ""));
          }

          const target = event.target as HTMLInputElement;
          return updateTerms(target?.value || "");
        };

        element.addEventListener(on, handleEvent);
        onCleanup(() => element.removeEventListener(on, handleEvent));
      },
    ),
  );
}

function SearchResults(_: unknown, options: ComponentOptions) {
  const { element } = options;
  const [state, { getResultsMarkup, updatePage }] = useSearch(element);

  let isFirstRender = true;

  createEffect(
    on(
      () => state.textResults(),
      () => {
        if (isFirstRender) return;
        element.innerHTML = getResultsMarkup();
      },
    ),
  );

  onMount(() => {
    isFirstRender = false;
  });

  function handlePageAnchorClick(event: Event) {
    let target = event.target as HTMLElement | null;

    if (!target || target.tagName !== "A") {
      target = target?.closest<HTMLAnchorElement>("a[href*='page=']") || null;
    }

    if (!target) return;

    const href = target.getAttribute("href");
    if (!href?.includes("page=")) return;

    event.preventDefault();

    const url = new URL(`${window.location.origin}${href}`);
    const page = Number(url.searchParams.get("page")) || 1;
    updatePage(page);
  }

  element.addEventListener("click", handlePageAnchorClick);
  onCleanup(() => element.removeEventListener("click", handlePageAnchorClick));
}

customShadowlessElement(
  "search-section",
  {
    sectionId: "",
    terms: "",
    delay: 500,
    page: 1,
  },
  correctElementType(SearchSection),
);

customShadowlessElement(
  "search-listener",
  {
    on: "submit",
    preventDefault: true,
  },
  correctElementType(SearchListener),
);

customShadowlessElement(
  "search-results",
  {},
  correctElementType(SearchResults),
);
